/**
 * MessageChannel values for support-v2.
 * These match the `MessageChannel` type from `effect-api/shared` but are hardcoded
 * here to avoid a circular dependency (effect-api/shared → redo/model, so redo/model
 * cannot import from effect-api/shared).
 */
export const MESSAGE_CHANNELS_V3 = [
  "gmail",
  "outlook",
  "redoChat",
  "facebook",
  "facebookComments",
  "instagram",
  "instagramComments",
  "sms",
  "voice",
  "attentive",
  "postscript",
  "tiktok",
  "contactForm",
] as const;

export type MessageChannelV3 = (typeof MESSAGE_CHANNELS_V3)[number];

/**
 * ConversationStatus values for support-v2.
 * These match the `ConversationStatus` type from `effect-api/shared`.
 * V3 supports more statuses than v2 (adds HIDDEN, ARCHIVED, DELETED).
 */
export const CONVERSATION_STATUSES_V3 = [
  "OPEN",
  "CLOSED",
  "SNOOZED",
  "IN_PROGRESS",
  "HIDDEN",
  "ARCHIVED",
  "DELETED",
] as const;

export type ConversationStatusV3 = (typeof CONVERSATION_STATUSES_V3)[number];

/**
 * Per-thread AI engagement statuses. Source of truth for the DB column
 * (`ai_status` enum in
 * `cockroachdb/schema/definitions/tables/shopper-ai/conversation/thread/product-data/ai-status-enum.sql`)
 * and for the rules-v3 `AI_STATUS` condition. `effect-api/shared`
 * re-exports this constant and wraps it in an effect `Schema`; the rules
 * engine cannot import from `effect-api/shared` (circular), so the
 * canonical declaration lives here in `redo/model`.
 */
export const AI_STATUSES = ["ACTIVE", "HANDED_OFF"] as const;
export type AiStatus = (typeof AI_STATUSES)[number];

/**
 * Time units for rules-v3 conditions and actions that take a `value + unit`
 * duration (response time, last-order time, merge-support-ticket age window).
 *
 * Defined here in `types.ts` rather than `schemas.ts` because `rules/actions.ts`
 * also needs the type and importing `schemas.ts` would create a cycle
 * (`rules/schemas → rules/actions → rules-v3/schemas → rules/schemas`).
 */
export enum TimeUnit {
  MINUTES = "minutes",
  HOURS = "hours",
  DAYS = "days",
}
